<template>
  <div>
    <div class="space-y-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
          <span class="material-icons-round">emoji_events</span>
        </span>
        <div>
          <h2 class="text-lg font-semibold text-gray-800">Promo History</h2>
          <p class="text-sm text-gray-500">Track promotional campaigns and usage.</p>
        </div>
      </div>

      <div class="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center space-x-1 text-gray-500">
            <span class="text-base material-icons-round">filter_list</span>
            <span class="text-xs font-semibold tracking-widest uppercase"
              >Filters</span
            >
          </div>
          <export-button
            :downloadUrl="reportDownloadUrl"
            :working="loading"
          ></export-button>
        </div>

        <div class="flex flex-wrap items-end gap-4">
          <!-- Status -->
          <div class="w-full sm:w-48">
            <label
              class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
              >Status</label
            >
            <select
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
              v-model="selectedFilter"
            >
              <option value="all">All</option>
              <option
                v-for="status in statuses"
                :key="`status-${status}`"
                :value="status"
              >
                {{ status }}
              </option>
            </select>
          </div>

          <!-- Passenger -->
          <div class="relative w-full sm:w-56">
            <label
              class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
              >Passenger</label
            >
            <div class="relative mt-1.5">
              <span
                class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
              >
                <span class="text-base text-gray-500 material-icons-round"
                  >search</span
                >
              </span>
              <input
                v-model="selectedPassenger"
                @keyup="searchPassenger"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search passenger..."
              />
            </div>
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="passengerSearchResults.length"
                class="absolute inset-x-0 z-50 mt-2"
              >
                <div
                  class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64"
                >
                  <ul>
                    <li
                      v-for="(result, index) in passengerSearchResults"
                      :key="result._id"
                      @click="selectPassenger(index)"
                      class="px-3 py-2 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                    >
                      <p class="text-sm font-semibold text-gray-800">
                        {{ `${result.firstName} ${result.lastName}` }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ result.phoneNumber }}
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </transition>
          </div>

          <!-- Driver -->
          <div class="relative w-full sm:w-56">
            <label
              class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
              >Driver</label
            >
            <div class="relative mt-1.5">
              <span
                class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
              >
                <span class="text-base text-gray-500 material-icons-round"
                  >search</span
                >
              </span>
              <input
                v-model="selectedDriver"
                @keyup="searchDriver"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search driver..."
              />
            </div>
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="driverSearchResults.length"
                class="absolute inset-x-0 z-50 mt-2"
              >
                <div
                  class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64"
                >
                  <ul>
                    <li
                      v-for="(result, index) in driverSearchResults"
                      :key="result._id"
                      @click="selectDriver(index)"
                      class="px-3 py-2 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                    >
                      <p class="text-sm font-semibold text-gray-800">
                        {{ `${result.firstName} ${result.lastName}` }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ result.phoneNumber }}
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </transition>
          </div>

          <!-- Date range -->
          <div class="w-full sm:w-auto">
            <label
              class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
              >Date Range</label
            >
            <div
              class="flex items-center h-10 mt-1.5 bg-white border border-gray-300 shadow-sm rounded-xl"
            >
              <span
                class="pl-3 pr-1 text-base text-gray-500 material-icons-round"
                >event</span
              >
              <v-date-picker v-model="range" is-range>
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="flex items-center">
                    <input
                      :value="inputValue.start"
                      v-on="inputEvents.start"
                      placeholder="From"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                    <span
                      class="mx-1 text-base text-gray-300 material-icons-round"
                      >arrow_forward</span
                    >
                    <input
                      :value="inputValue.end"
                      v-on="inputEvents.end"
                      placeholder="To"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                  </div>
                </template>
              </v-date-picker>
              <button
                v-if="range.start || range.end"
                @click="range = { start: null, end: null }"
                type="button"
                title="Clear dates"
                class="flex items-center justify-center w-8 h-8 mx-1 text-gray-400 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
              >
                <span class="text-lg material-icons-round">close</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        class="mt-4 overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm"
      >
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="bg-gray-100 border-b border-gray-200">
                <th
                  v-for="col in [
                    'Passenger',
                    'Driver',
                    'Invitee',
                    'Invite Type',
                    'Status',
                    'Trips Completed',
                    'Date/Time',
                  ]"
                  :key="`col-${col}`"
                  class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
                >
                  {{ col }}
                </th>
              </tr>
            </thead>
            <tbody class="relative" v-if="promos.length">
              <tr
                v-for="(promo, index) in promos"
                :key="promo._id"
                class="transition-colors duration-150 hover:bg-taxiye-50"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
              >
                <!-- Passenger -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div
                    v-if="promo.passenger"
                    class="flex items-center space-x-3"
                  >
                    <span
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                      :class="avatarClass(personLabel(promo.passenger))"
                      >{{ initials(personLabel(promo.passenger)) }}</span
                    >
                    <div>
                      <p class="font-medium text-gray-900">
                        {{ personLabel(promo.passenger, "Unknown") }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ promo.passenger.phoneNumber || "—" }}
                      </p>
                    </div>
                  </div>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <!-- Driver -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div v-if="promo.driver" class="flex items-center space-x-3">
                    <span
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                      :class="avatarClass(personLabel(promo.driver))"
                      >{{ initials(personLabel(promo.driver)) }}</span
                    >
                    <div>
                      <p class="font-medium text-gray-900">
                        {{ personLabel(promo.driver, "Unknown") }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ promo.driver.phoneNumber || "—" }}
                      </p>
                    </div>
                  </div>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <!-- Invitee -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    v-if="promo.inviteePhoneNumber"
                    class="inline-flex px-2 py-1 font-mono text-xs font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-md"
                  >
                    {{ promo.inviteePhoneNumber }}
                  </span>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <!-- Invite type -->
                <td
                  class="px-5 py-4 text-gray-600 capitalize whitespace-no-wrap"
                >
                  {{ promo.type || "—" }}
                </td>
                <!-- Status -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    class="inline-flex items-center px-2 py-1 text-xs font-semibold leading-none rounded-full"
                    :class="statusClass(promo.status).badge"
                  >
                    <span
                      class="w-2 h-2 mr-2 rounded-full"
                      :class="statusClass(promo.status).dot"
                    ></span>
                    {{ promo.status || "—" }}
                  </span>
                </td>
                <!-- Trips completed -->
                <td
                  class="px-5 py-4 font-medium text-gray-900 whitespace-no-wrap"
                >
                  {{ promo.tripCount }}
                </td>
                <!-- Date/Time -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  <div class="flex items-center space-x-2">
                    <i class="text-sm text-gray-400 material-icons-round"
                      >schedule</i
                    >
                    <span>{{ formatDate(promo.createdAt) }}</span>
                  </div>
                </td>
              </tr>
              <div
                v-if="loading"
                class="absolute inset-0 flex items-center justify-center w-full h-full bg-white bg-opacity-50"
              >
                <i class="text-5xl text-taxiye-500 material-icons-round spin"
                  >sync</i
                >
                <span class="invisible block">Loading...</span>
              </div>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="7" class="py-16 text-center bg-white">
                  <div v-if="loading" class="inline-block">
                    <i
                      class="text-5xl text-taxiye-500 material-icons-round spin"
                      >sync</i
                    >
                    <span class="invisible block">Loading...</span>
                  </div>
                  <div v-else class="flex flex-col items-center text-gray-400">
                    <span
                      class="flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-gray-100"
                    >
                      <i class="text-3xl material-icons-round">inbox</i>
                    </span>
                    <span class="text-sm font-medium text-gray-500"
                      >No promos found</span
                    >
                    <span class="mt-1 text-xs text-gray-400"
                      >Try adjusting your filters</span
                    >
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex items-end justify-between">
        <pagination
          class="flex space-x-2"
          :currentPage="currentPage"
          :total="total"
          @goto="goToPage"
          @setPerPage="setPerPage"
          :perPage="perPage"
          :start="start"
          :end="end"
          :count="paginator.count"
        ></pagination>
      </div>
    </div>
  </div>
</template>

<script>
// import moment from "moment";
import PaginationMixin from "@/mixins/Pagination";
import ExportButton from "@/components/ExportButton";
import Pagination from "@/components/Pagination";

export default {
  mixins: [PaginationMixin],
  data() {
    return {
      promos: [],
      loading: true,
      selectedFilter: "all",

      selectedPassenger: "",
      passengerSearchResults: [],
      passengerId: null,

      selectedDriver: "",
      driverSearchResults: [],
      driverId: null,

      range: {
        start: null,
        end: null,
      },

      statuses: ["INVITED", "ACTIVE", "USED"],
    };
  },
  components: {
    ExportButton,
    Pagination,
  },
  watch: {
    currentPage() {
      this.getTripRequests();
    },
    perPage() {
      this.currentPage === 1 ? this.getTripRequests() : (this.currentPage = 1);
    },
    selectedFilter() {
      this.getTripRequests();
    },
    range: {
      handler() {
        this.getTripRequests();
      },
      deep: true,
    },
    $route() {
      if (this.$route.query.status) {
        this.selectedFilter = this.$route.query.status;
      }
    },
  },
  created() {
    if (this.$route.query.status) {
      this.selectedFilter = this.$route.query.status;
    }
    if (this.$route.query.page) {
      this.currentPage = this.$route.query.page;
    }
    if (this.$route.query.passenger) {
      this.passengerId = this.$route.query.passenger;
      this.$misc
        .get("users/" + this.$route.query.passenger)
        .then(({ data: { firstName, lastName, phoneNumber } }) => {
          this.selectedPassenger = `${firstName} ${lastName} - ${phoneNumber}`;
        });
    }
    if (this.$route.query.driver) {
      this.driverId = this.$route.query.driver;
      this.$misc.get("drivers/" + this.$route.query.driver).then(({ data }) => {
        const { firstName, lastName, phoneNumber } = data.driver;
        this.selectedDriver = `${firstName} ${lastName} - ${phoneNumber}`;
      });
    }

    this.getTripRequests();
  },
  computed: {
    driverFilter() {
      return this.$route.query.driver || null;
    },
    riderFilter() {
      return this.$route.query.passenger || null;
    },
    reportDownloadUrl() {
      return (
        "promos/export?" +
        Object.entries({
          ...Object.fromEntries(
            Object.entries({
              driver: this.driverId ? this.driverId : undefined,
              passenger: this.passengerId ? this.passengerId : undefined,
              status:
                this.selectedFilter && this.selectedFilter != "all"
                  ? this.selectedFilter
                  : undefined,
              ...(this.range || {}),
            }).filter(([key, val]) => key && val)
          ),
        })
          .map(([key, value]) => [key, value].join("="))
          .join("&")
      );
    },
  },
  methods: {
    searchDriver(e) {
      if (e.target.value.trim() === "") {
        this.driverSearchResults = [];
        this.driverId = null;
        this.getTripRequests();
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.driverSearchResults = [];
          this.$misc
            .get("drivers/search?vehicle=true&limit=5&q=" + name)
            .then(({ data }) => {
              this.driverSearchResults = data;
            })
            .catch(err => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.driverSearchResults = [];
        }
      }
    },

    searchPassenger(e) {
      if (e.target.value.trim() === "") {
        this.passengerSearchResults = [];
        this.passengerId = null;
        this.getTripRequests();
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.passengerSearchResults = [];
          this.$misc
            .get("users/search?vehicle=true&limit=5&q=" + name)
            .then(({ data }) => {
              this.passengerSearchResults = data;
            })
            .catch(err => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.passengerSearchResults = [];
        }
      }
    },

    selectDriver(index) {
      let driver = this.driverSearchResults[index];
      this.selectedDriver = `${driver.firstName} ${driver.lastName} - ${driver.phoneNumber}`;
      this.driverId = driver._id;
      this.driverSearchResults = [];
      const query = { ...this.$route.query, driver: this.driverId };
      this.$router.push({ query });
      this.getTripRequests();
    },

    selectPassenger(index) {
      let user = this.passengerSearchResults[index];
      this.selectedPassenger = `${user.firstName} ${user.lastName} - ${user.phoneNumber}`;
      this.passengerId = user._id;
      this.passengerSearchResults = [];
      const query = { ...this.$route.query, passenger: this.passengerId };
      this.$router.push({ query });

      this.getTripRequests();
    },

    formatDate(date) {
      // return moment(date).format("MMM D, YYYY HH:mm");
      return date ? date.replace("T", " ").slice(0, 16) : "";
    },
    personLabel(person, fallback = "") {
      if (!person) return fallback;
      const name = `${person.firstName || ""} ${person.lastName || ""}`.trim();
      if (!name) return fallback;
      return name
        .split(" ")
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .filter(Boolean)
        .map(part => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    avatarClass(name) {
      const palette = [
        "bg-taxiye-100 text-taxiye-600",
        "bg-indigo-50 text-indigo-600",
        "bg-blue-50 text-blue-600",
        "bg-green-50 text-green-600",
        "bg-purple-50 text-purple-600",
        "bg-pink-50 text-pink-600",
      ];
      let hash = 0;
      for (let i = 0; i < (name || "").length; i++) {
        hash = (hash + name.charCodeAt(i)) % palette.length;
      }
      return palette[hash];
    },
    statusClass(status) {
      const classes = {
        INVITED: {
          badge: "text-yellow-700 bg-yellow-100",
          dot: "bg-yellow-500",
        },
        ACTIVE: { badge: "text-green-700 bg-green-100", dot: "bg-green-500" },
        USED: { badge: "text-blue-700 bg-blue-100", dot: "bg-blue-500" },
      };
      return (
        classes[status] || {
          badge: "text-gray-600 bg-gray-100",
          dot: "bg-gray-400",
        }
      );
    },
    getTripRequests() {
      this.loading = true;
      var path = `promos?limit=${this.perPage}&page=${this.currentPage}`;

      if (this.driverId) {
        path += "&driver=" + this.driverId;
      }

      if (this.passengerId) {
        path += "&passenger=" + this.passengerId;
      }

      if (this.selectedFilter && this.selectedFilter != "all") {
        path += "&status=" + this.selectedFilter;
      }

      if (this.range && this.range.start && this.range.end) {
        path += "&from=" + this.range.start + "&to=" + this.range.end;
      }

      this.$misc
        .get(path)
        .then(response => {
          this.promos = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count,
          };
        })
        .catch(err => console.log(err))
        .finally(() => (this.loading = false));
    },
  },
};
</script>
